import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import type {
  Ceiling,
  CeilingSection,
  GeneratedCeiling,
  GeneratedSubCeiling,
  Vaulted,
} from "building-types/ceilings/ceiling";
import {
  CeilingBoundaryType,
  CeilingOutlineError,
  CeilingOutlineErrorMessage,
  CeilingRidgeOrientation,
  CeilingType,
  HeightType,
} from "building-types/ceilings/ceiling";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { CeilingHandle, RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level, LevelAttributes } from "building-types/levels/levelTypes";
import { isExteriorSlot } from "building-types/materials/exteriorMaterialSlotNames";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import type { InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Wall } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNull, assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import { floorAssemblyBaseOffset, floorAssemblyTop } from "doc/elements/floor/floorAssembly";
import { docEntityGetChildrenFromTable } from "doc/entity/docEntityGetChildrenFromTable";
import { evalFormulas } from "doc/formula/evalFormulas";
import { materialSrcFromPalette } from "doc/materialLookup";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import type { Face } from "geom/brep";
import { faceCopy, faceNew, faceReverse } from "geom/brep";
import { closestPointPolyLine2 } from "geom/closestPoint";
import { PolyLine2PolyLine2ContainmentClass, classifyContainmentPolyLine2PolyLine2 } from "geom/containment";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, WindingDir } from "geom/core";
import { extrudePolyLine2 } from "geom/extrude";
import { geomToFaces } from "geom/geom";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { intersectRay3PlaneWithNormal } from "geom/intersectRay3";
import { offsetPolyLine2 } from "geom/offset";
import { planeByThreePoints, planeDistanceTo, planeNewXY, planeNormal, planeProjectPolyLine3 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { PolygonRoundingMode, polygonSubtract } from "geom/polyclip/polyClip";
import {
  maxPolyAlignedToEdge,
  polyLine2AABB2,
  polyLine2Area,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2FromAABB2,
  polyLine2IsClosed,
  polyLine2IsSelfIntersecting,
  polyLine2MergeCollinearEdges,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
  polyLine2ReverseCopy,
  polyLine2Shift,
  polyLine2WindingDir,
} from "geom/polyLine2";
import { polyLine2MaximalRectangle } from "geom/polyLine2MaximalRect";
import { polygonArea } from "geom/polyLine2Polygon";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3Close, polyLine3New, polyLine3RemoveDuplicateNeighbors } from "geom/polyLine3";
import { ray3New } from "geom/ray3";
import { splitPolyLineAtPoints } from "geom/trim";
import { vec2Dist, vec2Lerp } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3Dist, vec3Perpendicular } from "geom/vec3";
import { getDatumAbstraction } from "../elements/datums/datumAbstractions";
import { trimCeilingFaceWithHalfStoryRoofs } from "../elements/walls/halfStories";
import { computeThresholdsForVoidWall } from "../elements/walls/wallOpeningThresholds";
import { applyTransformToOutline } from "../utils/componentTransforms";
import { addDoubleSidedFace } from "./doubleSidedFace";

interface FloorElevationAndCeilingHeight {
  floorElevation: number;
  ceilingHeight: number;
}

export function getFloorElevationAndCeilingHeight(
  room: Room,
  roomFloorAssembly: FloorAssembly,
): FloorElevationAndCeilingHeight {
  const { topOfWall } = room.level;
  const { ceilingHeightOffset } = room;
  const floorElevationOffset = floorAssemblyBaseOffset(roomFloorAssembly);
  const floorElevation = floorAssemblyTop(roomFloorAssembly, room.level);
  const ceilingHeight = topOfWall + ceilingHeightOffset - floorElevationOffset;

  return {
    floorElevation,
    ceilingHeight,
  };
}

function accumulateVoidWallsCeilingOutlines(roomWall: Wall, outlines: PolyLine2Polygon[]) {
  if (roomWall.wallType === WallType.VOID) {
    computeThresholdsForVoidWall(roomWall, outlines);
  }
}

/**
 * Returns the integerId to use for a ceiling: COMPONENT_INSTANCE when the room
 * is part of a component instance, otherwise the ceiling handle.
 */
/** A ceiling inside a component instance selects the instance, like the room it belongs to. */
export function getCeilingPickHandle(room: Pick<Room, "instanceId">, ceilingHandle: CeilingHandle): DocEntityHandle {
  return room.instanceId ? { type: "COMPONENT_INSTANCE", id: room.instanceId } : ceilingHandle;
}

export function getCeilingsForLevel(
  levelId: string,
  levels: Table<Level>,
  levelsAttributes: Table<LevelAttributes>,
  datumAbstractions: Table<DatumAbstraction>,
  roofs: Table<GeneratedRoofSystem>,
  rooms: Table<Room>,
  roomFloorAssemblies: Table<FloorAssembly>,
  docCeilings: Table<Ceiling>,
  roomsByLevel: Building["roomsByLevel"],
  blocks: Table<Block>,
  interiorPalette: InteriorPalette,
  exteriorPalette: ExteriorPalette,
  roomOutlineTable: Table<RoomOutlines>,
  ceilingsByLevel: RWTable<string[]>,
  ceilings: { [id: string]: GeneratedCeiling },
  walls: Table<WallSystem>,
  mirror: boolean,
  mirrorTranslationX: number,
  optionChoices: OptionChoices,
  entityDisabledByGroup: Table<boolean>,
  issues: BuildingGenerationIssue[],
) {
  const level = levels[levelId];
  const { floorElevation: baseFloorElevation, componentInstanceChain } = level;

  const basePlane = planeNewXY(0, 0, baseFloorElevation);

  const upperOpenBelow =
    level.levelAbove !== undefined
      ? roomsByLevel[level.levelAbove]
          .map((roomId) => rooms[roomId])
          .filter((room) => room.attributes.openToBelow)
          .map((room) => roomOutlineTable[room.id].INSIDE_OF_MASS_IGNORE_VOID)
      : [];

  const upperVoidWallPolys: PolyLine2Polygon[] = [];

  const levelAbove = level.levelAbove;
  if (levelAbove !== undefined) {
    roomsByLevel[levelAbove]
      .map((roomId) => rooms[roomId])
      .filter((room) => room.attributes.openToBelow)
      .map((room) => walls[levelAbove].wallIndex.wallsInRoom(room))
      .forEach((roomWalls) =>
        roomWalls.forEach((roomWall) => accumulateVoidWallsCeilingOutlines(roomWall, upperVoidWallPolys)),
      );
  }

  const updatedUpperOpenBelow: PolyLine2Polygon[] = [];
  upperOpenBelow.forEach((openPoly) => {
    if (!openPoly) {
      return;
    }

    const diffPolys = polygonSubtract({
      pls: [[openPoly], ...upperVoidWallPolys],
      roundingMode: PolygonRoundingMode.SNAP_ROUND,
    });
    for (const poly of diffPolys) {
      if (polygonArea(poly) > DIST_EPSILON_SQUARED) {
        updatedUpperOpenBelow.push(poly);
      }
    }
  });

  const levelCeilingRoomIds: string[] = [];

  for (const roomId of roomsByLevel[levelId]) {
    const room = rooms[roomId];

    const { roomEnclosure, openToAbove } = room.attributes;

    if (roomEnclosure === RoomEnclosure.NONE || openToAbove) {
      continue;
    }

    const { isHalfStory } = blocks[room.parentId];
    const baseRoomId = getSourceRoomId(room);
    const roomFloorAssembly = roomFloorAssemblies[room.id];
    const { floorElevation, ceilingHeight } = getFloorElevationAndCeilingHeight(room, roomFloorAssembly);
    const ceilingHeightBase = ceilingHeight - room.ceilingHeightOffset;

    let shadowFace: Face | undefined;
    if (!isHalfStory) {
      const ceilingOutlineNoHoles = [polyLine2ReverseCopy(room.outline)];
      const ceilingPlaneNoHoles = { ...basePlane, originZ: floorElevation + ceilingHeight - 0.001 };
      shadowFace = faceReverse(faceNew(ceilingOutlineNoHoles, ceilingPlaneNoHoles));
      assertIsNotNull(shadowFace);
    }

    let ceilingRoomOutline: PolyLine2 | undefined = room.outline;
    if (roomOutlineTable[roomId]) {
      ceilingRoomOutline = roomOutlineTable[roomId].INSIDE_OF_MASS_IGNORE_VOID;
    }

    assertIsNotNullOrUndefined(ceilingRoomOutline);

    const ceilingOutline = [polyLine2ReverseCopy(ceilingRoomOutline)];

    const generatedSubCeilings: GeneratedSubCeiling[] = [];

    const roomSubCeilings = docEntityGetChildrenFromTable(docCeilings, baseRoomId);

    const subCeilingOutlines: PolyLine2[] = [];
    let isSubceilingTypeNoneProcessed: boolean = false;
    let hasCeiling = false;
    let flatCeiling: Ceiling | undefined;
    for (const subCeilingId in roomSubCeilings) {
      let subCeiling = roomSubCeilings[subCeilingId];

      // build ceiling plane

      let ceilingVerticalOffset = 0;
      if (subCeiling.ceilingDatum) {
        const ceilingDatumAbstraction = getDatumAbstraction({
          datumUsage: subCeiling.ceilingDatum,
          datumAbstractions,
          requestedUsageType: DatumVerticalUsageType.CEILING,
          componentInstanceChain,
        });
        const ceilingHeight = ceilingDatumAbstraction?.resolveVerticalDatumRelativeToLevel({
          level,
          levelAttributes: levelsAttributes[levelId],
          extraOffset: subCeiling.ceilingDatum.offset,
        });

        if (ceilingHeight !== undefined) {
          ceilingVerticalOffset = ceilingHeight - level.topOfWall;
        }
      }

      const subceilingHeight = ceilingHeightBase + ceilingVerticalOffset;
      const subceilingPlane = { ...basePlane, originZ: floorElevation + subceilingHeight - 0.001 };

      subCeiling = evalFormulas(subCeiling, (id) => optionChoices[id], undefined, entityDisabledByGroup);
      if (subCeiling.disabled) {
        continue;
      }
      if (subCeiling.type !== CeilingType.NONE) {
        hasCeiling = true;
      }
      if (subCeiling.type === CeilingType.FLAT) {
        if (!flatCeiling) {
          flatCeiling = subCeiling;
        }
        continue;
      }

      const { labels } = subCeiling;
      const ceilingHandle: CeilingHandle = { id: subCeiling.id, type: "CEILING" };
      const pickHandle = getCeilingPickHandle(room, ceilingHandle);

      let outline: PolyLine2 | undefined;
      let offset = 0;
      switch (subCeiling.type) {
        case CeilingType.NONE: {
          if (isSubceilingTypeNoneProcessed) {
            continue;
          }
          outline = polyLine2MergeCollinearEdges(polyLine2Copy(ceilingRoomOutline));
          isSubceilingTypeNoneProcessed = true; //only one NONE subceiling allowed otherwise it will create negative areas
          /* Note: this is not an ideal workflow, we should restric this at the product UI level but nevertheless this works */
          break;
        }
        case CeilingType.VAULTED:
        case CeilingType.TRAY_SIMPLE_OFFSET: {
          offset = subCeiling.offset;
          const { boundaryType } = subCeiling;
          const sanitizedOffset = offset > 1 ? offset : 0;
          const roomOutline = polyLine2MergeCollinearEdges(polyLine2Copy(ceilingRoomOutline));
          if (boundaryType === CeilingBoundaryType.MAX_RECTANGLE) {
            const { maxRectAsPolyLine2 } = polyLine2MaximalRectangle(roomOutline);
            outline = offsetPolyLine2(maxRectAsPolyLine2, -sanitizedOffset);
          } else {
            outline = offsetPolyLine2(roomOutline, -sanitizedOffset);
          }
          break;
        }
        case CeilingType.TRAY_FREEFORM: {
          if (subCeiling.outline) {
            const { componentTransform } = room;
            if (componentTransform) {
              outline = applyTransformToOutline(componentTransform.matrix, subCeiling.outline);
            } else if (mirror) {
              outline = polyLine2ReflectOverYAxis(polyLine2Copy(subCeiling.outline));
              polyLine2IsClosed(outline) ? polyLine2Shift(polyLine2Reverse(outline), 1) : polyLine2Reverse(outline);
              polyLine2Move(outline, mirrorTranslationX, 0);
            } else {
              outline = polyLine2Copy(subCeiling.outline);
            }
          }
          break;
        }
      }

      let ceilingOutlineError: CeilingOutlineError | undefined;

      if (!outline) {
        if (subCeiling.type === CeilingType.TRAY_FREEFORM) {
          ceilingOutlineError = CeilingOutlineError.OUTLINE_NOT_DRAWN;
        } else {
          ceilingOutlineError = CeilingOutlineError.OUTLINE_UNKNOWN_ERROR;
        }
      } else if (outline && ceilingRoomOutline) {
        switch (classifyContainmentPolyLine2PolyLine2(outline, ceilingRoomOutline)) {
          case PolyLine2PolyLine2ContainmentClass.OUTSIDE:
            ceilingOutlineError = CeilingOutlineError.OUTLINE_OUTSIDE_OF_ROOM;
            break;
          case PolyLine2PolyLine2ContainmentClass.CROSSING:
          case PolyLine2PolyLine2ContainmentClass.OUTSIDE_COINCIDENT:
            ceilingOutlineError = CeilingOutlineError.OUTLINE_INTERSECTS_ROOM_OUTLINE;
            break;
          default: {
            if (polyLine2Area(outline) < DIST_EPSILON) {
              ceilingOutlineError = CeilingOutlineError.OUTLINE_ZERO_AREA;
            } else if (polyLine2IsSelfIntersecting(outline)) {
              ceilingOutlineError = CeilingOutlineError.OUTLINE_SELF_INTERSECTING;
            }
          }
        }
      }

      if (ceilingOutlineError) {
        issues.push({
          type: BuildingGenerationIssueType.CEILING_GENERATION_ISSUE,
          handle: { id: room.id, type: "ROOM" } as RoomHandle,
          message: `Ceiling geometry for ${room.name} at ${room.level.name} has ${CeilingOutlineErrorMessage[ceilingOutlineError]}`,
        });
        if (subCeiling.type !== CeilingType.NONE) {
          //incase of NONE we would like the algorithm to do the subtraction
          generatedSubCeilings.push({
            entityType: "CEILING",
            pickHandle,
            handle: ceilingHandle,
            labels,
            ceilingOutlineError,
            outline: outline ? polyLine2Copy(outline) : undefined,
            sections: [],
            type: subCeiling.type,
          });
          continue;
        }
      }

      assertIsNotUndefined(outline);

      subCeilingOutlines.push(polyLine2Copy(outline));

      let faces: Face[] = [];
      switch (subCeiling.type) {
        case CeilingType.NONE: {
          break;
        }
        case CeilingType.TRAY_SIMPLE_OFFSET:
        case CeilingType.TRAY_FREEFORM: {
          let resolvedHeightAboveFinishFloor = 0;
          switch (subCeiling.heightType) {
            case HeightType.ABOVE_FINISH_FLOOR: {
              resolvedHeightAboveFinishFloor = subCeiling.height;
              break;
            }
            case HeightType.OFFSET_FROM_CEILING: {
              resolvedHeightAboveFinishFloor = subceilingHeight + subCeiling.height;
              break;
            }
          }
          const heightDiffFromCeilingPlane = resolvedHeightAboveFinishFloor - subceilingHeight;
          const extrusion = extrudePolyLine2(
            polyLine2ReverseCopy(outline),
            subceilingPlane,
            0,
            0,
            heightDiffFromCeilingPlane,
          );
          faces = geomToFaces(extrusion).map((x) => (heightDiffFromCeilingPlane < 0 ? x : faceReverse(x)));
          faces.shift(); // remove bottom face
          break;
        }
        case CeilingType.VAULTED: {
          faces = buildVaultedCeiling(subCeiling, room, outline, { ...subceilingPlane });
          break;
        }
      }

      const subSections: CeilingSection[] = [];

      for (const face of faces) {
        const materialSlot = room.ceilingMaterial || room.attributes.exteriorCeilingMaterial || "ceiling0";
        const designMaterialId = isExteriorSlot(materialSlot)
          ? materialSrcFromPalette(exteriorPalette, materialSlot)
          : materialSrcFromPalette(interiorPalette, materialSlot, room.type);

        if (isHalfStory) {
          if (
            !trimCeilingFaceWithHalfStoryRoofs({
              face,
              roofs,
            })
          ) {
            continue;
          }
        }

        if (face) {
          let subShadowFace: Face | undefined;
          if (!isHalfStory) {
            subShadowFace = faceReverse(faceCopy(face));
          }

          subSections.push({
            ceilingFaces: addDoubleSidedFace(face),
            shadowFace: subShadowFace,
            materialSlot,
            designMaterialId,
            roomId: room.id,
          });
        }
      }

      generatedSubCeilings.push({
        entityType: "CEILING",
        pickHandle,
        handle: ceilingHandle,
        labels,
        ceilingOutlineError,
        outline: outline ? polyLine2Copy(outline) : undefined,
        sections: subSections,
        type: subCeiling.type,
      });
    }

    if (subCeilingOutlines?.length > 0 && !!subCeilingOutlines[0]) {
      updatedUpperOpenBelow.push(...[subCeilingOutlines]);
    }

    const materialSlot = room.ceilingMaterial || room.attributes.exteriorCeilingMaterial || "ceiling0";
    const designMaterialId = isExteriorSlot(materialSlot)
      ? materialSrcFromPalette(exteriorPalette, materialSlot)
      : materialSrcFromPalette(interiorPalette, materialSlot, room.type);

    let ceilingFaces: Face[] = [];
    if (hasCeiling) {
      const polys: PolyLine2Polygon[] =
        updatedUpperOpenBelow.length > 0
          ? polygonSubtract({ pls: [ceilingOutline, ...updatedUpperOpenBelow] })
          : [ceilingOutline];
      const ceilingPlane = { ...basePlane, originZ: floorElevation + ceilingHeight - 0.001 };

      const flatFaces = polys.reduce((faces, poly) => {
        if (!poly) {
          return faces;
        }

        const face = faceReverse(faceNew(poly, { ...ceilingPlane }));

        if (
          isHalfStory &&
          !trimCeilingFaceWithHalfStoryRoofs({
            face,
            roofs,
          })
        ) {
          return faces;
        }

        // Generate ceilings as double-sided so that they show up correctly on 3D viewports.
        addDoubleSidedFace(face, faces);

        return faces;
      }, [] as Face[]);

      if (flatCeiling) {
        const handle: CeilingHandle = { id: flatCeiling.id, type: "CEILING" };
        generatedSubCeilings.push({
          entityType: "CEILING",
          pickHandle: getCeilingPickHandle(room, handle),
          handle,
          labels: flatCeiling.labels,
          ceilingOutlineError: undefined,
          outline: undefined,
          sections: [
            {
              ceilingFaces: flatFaces,
              materialSlot,
              designMaterialId,
              roomId: room.id,
            },
          ],
          type: flatCeiling.type,
        });
      } else {
        ceilingFaces = flatFaces;
      }
    }

    levelCeilingRoomIds.push(roomId);

    ceilings[roomId] = {
      section: {
        ceilingFaces,
        shadowFace,
        materialSlot,
        designMaterialId,
        roomId: room.id,
      },
      subCeilings: generatedSubCeilings,
    };
  }

  ceilingsByLevel[levelId] = levelCeilingRoomIds;
}

function buildVaultedCeiling(
  subCeiling: Vaulted & DocEntityBase<"CEILING">,
  room: Room,
  outline: PolyLine2,
  ceilingPlane: Plane,
): Face[] {
  let maxPoly: PolyLine2;
  let maxWidth: number;
  let maxHeight: number;
  // these indices represent the default horizontal ceiling plane
  let edgeAidx = 0;
  let edgeBidx = 2;
  if (subCeiling.boundaryType === CeilingBoundaryType.INSIDE_OF_FRAMING) {
    if (subCeiling.ridgeOrientation === CeilingRidgeOrientation.PARALLEL_TO_WALL) {
      ({ maxWidth, maxHeight, maxPoly } = maxPolyAlignedToEdge(
        room.outline,
        room.edgeIds.indexOf(subCeiling.ridgeOrientingWall ?? "1"),
      ));
      // debugPoly(maxPoly, "unrotated", 0xff0000);
    } else {
      const max = polyLine2AABB2(outline);
      maxPoly = polyLine2FromAABB2(max);
      maxWidth = aabb2Width(max);
      maxHeight = aabb2Height(max);
    }
  } else {
    const max = polyLine2MaximalRectangle(outline);
    maxPoly = max.maxRectAsPolyLine2;
    maxWidth = max.width;
    maxHeight = max.height;
  }
  if (subCeiling.ridgeOrientation === CeilingRidgeOrientation.AUTO) {
    if (maxWidth < maxHeight) {
      edgeAidx = 1;
      edgeBidx = 3;
    }
  } else {
    if (subCeiling.ridgeOrientation === CeilingRidgeOrientation.VERTICAL) {
      edgeAidx = 1;
      edgeBidx = 3;
    }
  }

  /*
      f0 --(f)-- f1
      |          |
      m0         m1
      |          |
      t1 --(t)-- t0
  */
  const maxPolyOffset = offsetPolyLine2(maxPoly, 0.5);
  const [f0x, f0y, f1x, f1y] = polyLine2Edge(maxPoly, edgeAidx);
  const [t0x, t0y, t1x, t1y] = polyLine2Edge(maxPoly, edgeBidx);

  assertIsNotNullOrUndefined(subCeiling);
  assertIsNotNullOrUndefined(subCeiling.ridge);
  assertIsNotNullOrUndefined(subCeiling.slope);

  // interpolate for ridgeline
  const [m0x, m0y] = vec2Lerp(f0x, f0y, t1x, t1y, subCeiling.ridge);
  const [m1x, m1y] = vec2Lerp(f1x, f1y, t0x, t0y, subCeiling.ridge);

  // calculate distance from first line endpoint to ridgeline
  const fDist = vec2Dist(f0x, f0y, m0x, m0y);
  const height = subCeiling.slope * fDist + ceilingPlane.originZ;

  const ridgeRay = intersectRay2PolyLine2(maxPolyOffset, m0x, m0y, m1x - m0x, m1y - m0y).map((ipt) =>
    closestPointPolyLine2(maxPolyOffset, ipt.x, ipt.y),
  )[0];

  const { x: r0x, y: r0y } = ridgeRay;
  const roomIntPts = intersectRay2PolyLine2(outline, r0x, r0y, m0x - m1x, m0y - m1y).map((ipt) =>
    closestPointPolyLine2(outline, ipt.x, ipt.y),
  );
  const ceilingSplit = splitPolyLineAtPoints(outline, roomIntPts);

  // construct planes for the ceiling
  const firstClgPlane = planeByThreePoints(
    f0x,
    f0y,
    ceilingPlane.originZ,
    m0x,
    m0y,
    height,
    f1x,
    f1y,
    ceilingPlane.originZ,
  );
  const secndClgPlane = planeByThreePoints(
    t1x,
    t1y,
    ceilingPlane.originZ,
    m0x,
    m0y,
    height,
    t0x,
    t0y,
    ceilingPlane.originZ,
  );

  const errs: string[] = [];
  return buildVaultedCeilingFaces(
    ceilingSplit,
    ceilingPlane,
    [0, 0, 1],
    [firstClgPlane, secndClgPlane],
    errs,
    subCeiling.includeEndcaps ?? true,
  );
}

function buildVaultedCeilingFaces(
  pl: PolyLine2,
  plane: Plane,
  extrudeNormal: Vec3,
  ceilingPlanes: (Plane | null)[],
  errs: string[],
  includeEndcaps: boolean,
): Face[] {
  const faces: Face[] = [];
  const [nx, ny, nz] = extrudeNormal;

  const z = plane.originZ;

  const caps: number[][] = new Array(ceilingPlanes.length);
  for (let dIdx = 0; dIdx < ceilingPlanes.length; dIdx++) {
    caps[dIdx] = [];
  }

  for (let i = 0; i < polyLine2EdgeCount(pl); i++) {
    const [ax, ay, bx, by] = polyLine2Edge(pl, i);
    const {
      originX: aRayX,
      originY: aRayY,
      originZ: aRayZ,
      dirX: aRayDirX,
      dirY: aRayDirY,
      dirZ: aRayDirZ,
    } = ray3New(ax, ay, z, nx, ny, nz);
    const {
      originX: bRayX,
      originY: bRayY,
      originZ: bRayZ,
      dirX: bRayDirX,
      dirY: bRayDirY,
      dirZ: bRayDirZ,
    } = ray3New(bx, by, z, nx, ny, nz);
    const aOrigin: Vec3 = [aRayX, aRayY, aRayZ];
    const bOrigin: Vec3 = [bRayX, bRayY, bRayZ];
    let aMin: Vec3 = [Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE];
    let bMin: Vec3 = [Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE];
    let aMinDist: number = Number.MAX_VALUE;
    let bMinDist: number = Number.MAX_VALUE;

    planeLoop: for (let dIdx = 0; dIdx < ceilingPlanes.length; dIdx++) {
      const destination = ceilingPlanes[dIdx];
      if (destination === null) {
        continue planeLoop;
      }
      const destNormal = planeNormal(destination);
      if (vec3Perpendicular(extrudeNormal, destNormal)) {
        continue planeLoop;
      }
      const [ndx, ndy, ndz] = destNormal;
      const isectA = intersectRay3PlaneWithNormal(
        aRayX,
        aRayY,
        aRayZ,
        aRayDirX,
        aRayDirY,
        aRayDirZ,
        destination.originX,
        destination.originY,
        destination.originZ,
        ndx,
        ndy,
        ndz,
      );

      if (isectA && valid(isectA)) {
        const aPossible: Vec3 = [...isectA];

        const aPossDist = v3d(aOrigin, aPossible);
        if (aPossDist < aMinDist) {
          aMin = aPossible;
          aMinDist = aPossDist;
        }
      }
      const isectB = intersectRay3PlaneWithNormal(
        bRayX,
        bRayY,
        bRayZ,
        bRayDirX,
        bRayDirY,
        bRayDirZ,
        destination.originX,
        destination.originY,
        destination.originZ,
        ndx,
        ndy,
        ndz,
      );

      if (isectB && valid(isectB)) {
        const bPossible: Vec3 = [...isectB];
        const bPossDist = v3d(bOrigin, bPossible);
        if (bPossDist < bMinDist) {
          bMin = bPossible;
          bMinDist = bPossDist;
        }
      }
    }
    if (aMinDist === Number.MAX_VALUE || bMinDist === Number.MAX_VALUE) {
      errs.push("Could not find destination plane for edge");
      continue;
    }

    for (let dIdx = 0; dIdx < ceilingPlanes.length; dIdx++) {
      const destination = ceilingPlanes[dIdx];
      if (destination === null) {
        continue;
      }
      if (planeDistanceTo(destination, aMin[0], aMin[1], aMin[2]) < DIST_EPSILON) {
        caps[dIdx].push(...aMin);
      }
    }

    if (aMinDist < DIST_EPSILON && bMinDist < DIST_EPSILON) {
      continue;
    }

    const facePts = [ax, ay, z, bx, by, z];
    if (bMinDist !== 0) {
      facePts.push(...bMin);
    }
    if (aMinDist !== 0) {
      facePts.push(...aMin);
    }
    facePts.push(ax, ay, z);

    if (includeEndcaps) {
      addVaultedCeilingVertical(faces, facePts);
    }
  }

  for (let dIdx = 0; dIdx < caps.length; dIdx++) {
    const destination = ceilingPlanes[dIdx];

    if (destination) {
      const cap = caps[dIdx];
      addVaultedCeilingCap(faces, destination, cap);
    }
  }

  return faces;
}

/**
 * wrapper for getting a distance between two pointts
 */
function v3d(a: Vec3, b: Vec3): number {
  return vec3Dist(a[0], a[1], a[2], b[0], b[1], b[2]);
}

/**
 * test if a point is valid
 */
function valid(a: Vec3): boolean {
  for (const n of a) {
    if (isNaN(n)) {
      return false;
    }
  }
  return true;
}

function addVaultedCeilingCap(faces: Face[], plane: Plane, pts: number[]): void {
  if (pts.length < 6) {
    return;
  }
  const pts3 = polyLine3Close(polyLine3RemoveDuplicateNeighbors(polyLine3New(pts)));

  addCeilingFace(faces, plane, pts3);
}

function addVaultedCeilingVertical(faces: Face[], pts: number[]): void {
  if (pts.length < 6) {
    return;
  }
  const pts3 = polyLine3RemoveDuplicateNeighbors(polyLine3New(pts));
  const facePlane = planeByThreePoints(pts3[6], pts3[7], pts3[8], pts3[3], pts3[4], pts3[5], pts3[0], pts3[1], pts3[2]);
  if (!facePlane) {
    return;
  }

  addCeilingFace(faces, facePlane, pts3);
}

function addCeilingFace(faces: Face[], plane: Plane, pts3: PolyLine3): void {
  const projected = planeProjectPolyLine3(plane, pts3);
  if (polyLine2WindingDir(projected) === WindingDir.CCW) {
    polyLine2Reverse(projected);
  }

  const face = faceNew([projected], plane);
  if (planeNormal(plane)[2] > 0) {
    faceReverse(face);
  }
  faces.push(face);
}
